import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { MessageCircle, X, Send, Mic, Loader2, Sparkles, ShoppingBag } from "lucide-react";
import { useVoiceInput } from "../hooks/useVoiceInput";
import { useCart } from "../context/CartContext";
import { menuHighlights, menuLookupKeys } from "../data/mock";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SID_KEY = "mm_concierge_sid";

const ADD_TAG_RE = /\[\[ADD:\s*(\{[^\]]*?\})\s*\]\]/g;

function stripTags(text) {
  // Remove complete action tags and hide any trailing partial tag mid-stream.
  return text.replace(ADD_TAG_RE, "").replace(/\[\[[^\]]*$/, "").trimEnd();
}

function parseAddTags(text) {
  const out = [];
  let m;
  const re = new RegExp(ADD_TAG_RE.source, "g");
  while ((m = re.exec(text)) !== null) {
    try {
      const j = JSON.parse(m[1]);
      const id = Number(j.id);
      const qty = Math.min(Math.max(Number(j.qty) || 1, 1), 10);
      if (id) out.push({ id, qty });
    } catch { /* malformed tag — skip */ }
  }
  return out;
}

function priceToNumber(p) {
  return parseFloat((p || "0").replace(/[^\d.]/g, "")) || 0;
}

function getSessionId() {
  let sid = localStorage.getItem(SID_KEY);
  if (!sid) {
    sid = (window.crypto?.randomUUID?.() || `sid-${Date.now()}-${Math.random().toString(36).slice(2)}`);
    localStorage.setItem(SID_KEY, sid);
  }
  return sid;
}

const QUICK_PROMPTS = [
  "What's spicy?",
  "Today's special?",
  "Best for kids?",
];

const WELCOME = "Hi, I'm Mama's menu concierge. Ask me anything about our dishes — or just tell me what you'd like and I'll add it to your order.";

export default function ConciergeChat() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([{ role: "assistant", content: WELCOME }]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const scrollRef = useRef(null);
  const voice = useVoiceInput({ onText: (t) => setInput(t) });
  const { add } = useCart();
  const menuMapRef = useRef(null);

  useEffect(() => {
    if (!open || menuMapRef.current) return;
    axios.get(`${API}/menu`).then(({ data }) => {
      const overrides = {};
      (data.items || []).forEach((o) => { overrides[o.id] = o; });
      const map = {};
      menuHighlights.forEach((m) => {
        const o = overrides[m.id] || {};
        map[m.id] = {
          lookup_key: menuLookupKeys[m.id],
          name: o.name || m.name,
          price: o.price != null ? Number(o.price) : priceToNumber(m.price),
          image: o.image != null ? o.image : m.image,
          sold_out: !!o.sold_out,
        };
      });
      (data.items || []).forEach((o) => {
        if (!map[o.id] && o.name && o.lookup_key) {
          map[o.id] = {
            lookup_key: o.lookup_key,
            name: o.name,
            price: Number(o.price) || 0,
            image: o.image || null,
            sold_out: !!o.sold_out,
          };
        }
      });
      menuMapRef.current = map;
    }).catch(() => { menuMapRef.current = {}; });
  }, [open]);

  const applyAddTags = (rawText) => {
    const map = menuMapRef.current || {};
    const added = [];
    parseAddTags(rawText).forEach(({ id, qty }) => {
      const item = map[id];
      if (!item || !item.lookup_key || item.sold_out) return;
      add({ lookup_key: item.lookup_key, name: item.name, price: item.price, image: item.image }, qty);
      added.push({ name: item.name, qty });
    });
    return added;
  };

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, open]);

  const send = async (text) => {
    const msg = (text || input).trim();
    if (!msg || streaming) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: msg }, { role: "assistant", content: "" }]);
    setStreaming(true);
    try {
      const res = await fetch(`${API}/concierge/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: getSessionId(), message: msg }),
      });
      if (!res.ok || !res.body) throw new Error(`HTTP ${res.status}`);
      const reader = res.body.getReader();
      const dec = new TextDecoder();
      let buf = "";
      let raw = "";
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += dec.decode(value, { stream: true });
        let idx;
        while ((idx = buf.indexOf("\n\n")) >= 0) {
          const rawFrame = buf.slice(0, idx);
          buf = buf.slice(idx + 2);
          const line = rawFrame.split("\n").find((l) => l.startsWith("data: "));
          if (!line) continue;
          const payload = line.slice(6);
          if (payload === "[DONE]") continue;
          try {
            const j = JSON.parse(payload);
            if (j.delta) {
              raw += j.delta;
              const display = stripTags(raw);
              setMessages((m) => {
                const copy = [...m];
                copy[copy.length - 1] = { role: "assistant", content: display };
                return copy;
              });
            } else if (j.error) {
              setMessages((m) => {
                const copy = [...m];
                copy[copy.length - 1] = { role: "assistant", content: j.error };
                return copy;
              });
            }
          } catch { /* partial frame — skip */ }
        }
      }
      const added = applyAddTags(raw);
      if (added.length) {
        setMessages((m) => {
          const copy = [...m];
          copy[copy.length - 1] = { ...copy[copy.length - 1], added };
          return copy;
        });
      }
    } catch (err) {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { role: "assistant", content: "Sorry — I couldn't reach the kitchen just now. Please try again." };
        return copy;
      });
    } finally {
      setStreaming(false);
    }
  };

  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        data-testid="concierge-open-btn"
        aria-label="Ask Mamamina — menu concierge chat"
        className="fixed bottom-24 md:bottom-6 right-4 md:right-6 z-40 inline-flex items-center gap-2 px-4 py-3 bg-[var(--mm-accent)] text-[var(--mm-bg)] shadow-lg hover:scale-105 transition-transform duration-300 rounded-full"
      >
        <MessageCircle size={18} />
        <span className="text-[0.7rem] tracking-[0.22em] uppercase font-semibold hidden sm:inline">Ask Mamamina</span>
      </button>
    );
  }

  return (
    <div
      data-testid="concierge-panel"
      className="fixed bottom-0 right-0 md:bottom-6 md:right-6 z-50 w-full md:w-[400px] h-[75vh] md:h-[560px] flex flex-col bg-[var(--mm-bg)] border border-[var(--mm-line)] md:shadow-2xl"
    >
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-[var(--mm-line)] bg-[var(--mm-bg-alt)]">
        <div className="flex items-center gap-2">
          <Sparkles size={15} className="text-[var(--mm-accent)]" />
          <div>
            <div className="font-display italic text-lg text-[var(--mm-cream)] leading-none">Ask Mamamina</div>
            <div className="text-[0.58rem] tracking-[0.24em] uppercase text-[var(--mm-cream-muted)] mt-0.5">Menu concierge</div>
          </div>
        </div>
        <button
          onClick={() => setOpen(false)}
          data-testid="concierge-close-btn"
          aria-label="Close chat"
          className="w-8 h-8 flex items-center justify-center text-[var(--mm-cream-muted)] hover:text-[var(--mm-accent)] transition-colors"
        >
          <X size={18} />
        </button>
      </div>

      {/* Messages */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-3" data-testid="concierge-messages">
        {messages.map((m, i) => (
          <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
            <div
              data-testid={`concierge-message-${i}`}
              className={`max-w-[85%] px-3.5 py-2.5 text-sm leading-relaxed whitespace-pre-wrap ${
                m.role === "user"
                  ? "bg-[var(--mm-accent)] text-[var(--mm-bg)]"
                  : "bg-[var(--mm-bg-alt)] border border-[var(--mm-line)] text-[var(--mm-cream)]"
              }`}
            >
              {m.content || (streaming && i === messages.length - 1 ? <Loader2 size={14} className="animate-spin text-[var(--mm-accent)]" /> : "")}
              {m.added?.length > 0 && (
                <div className="mt-2 pt-2 border-t border-[var(--mm-line)] space-y-1" data-testid={`concierge-added-${i}`}>
                  {m.added.map((a, k) => (
                    <div key={k} className="flex items-center gap-2 text-[0.72rem] text-[var(--mm-accent)]">
                      <ShoppingBag size={12} />
                      <span>Added {a.qty} × {a.name} to your order</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Quick prompts */}
      {messages.length <= 1 && (
        <div className="px-4 pb-2 flex flex-wrap gap-2">
          {QUICK_PROMPTS.map((q) => (
            <button
              key={q}
              onClick={() => send(q)}
              data-testid={`concierge-chip-${q.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              className="px-3 py-1.5 text-[0.66rem] tracking-[0.14em] uppercase border border-[var(--mm-line)] text-[var(--mm-cream-muted)] hover:border-[var(--mm-accent)] hover:text-[var(--mm-accent)] transition-colors"
            >
              {q}
            </button>
          ))}
        </div>
      )}

      {/* Input */}
      <div className="px-3 py-3 border-t border-[var(--mm-line)] flex items-center gap-2">
        {voice.supported && (
          <button
            onClick={voice.toggle}
            data-testid="concierge-mic-btn"
            aria-label={voice.recording ? "Stop recording" : "Speak your question"}
            title="Speak your question"
            className={`shrink-0 w-10 h-10 flex items-center justify-center border transition-all ${
              voice.recording
                ? "border-red-500 text-red-500 animate-pulse"
                : "border-[var(--mm-line)] text-[var(--mm-cream-muted)] hover:border-[var(--mm-accent)] hover:text-[var(--mm-accent)]"
            }`}
          >
            {voice.busy ? <Loader2 size={16} className="animate-spin" /> : <Mic size={16} />}
          </button>
        )}
        <input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") send(); }}
          placeholder={voice.recording ? "Listening…" : "Ask about the menu…"}
          data-testid="concierge-input"
          className="flex-1 bg-transparent border border-[var(--mm-line)] focus:border-[var(--mm-accent)] outline-none px-3 py-2.5 text-sm text-[var(--mm-cream)] placeholder:text-[var(--mm-cream)]/40"
        />
        <button
          onClick={() => send()}
          disabled={streaming || !input.trim()}
          data-testid="concierge-send-btn"
          aria-label="Send message"
          className="shrink-0 w-10 h-10 flex items-center justify-center bg-[var(--mm-accent)] text-[var(--mm-bg)] disabled:opacity-40 transition-opacity"
        >
          {streaming ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
        </button>
      </div>
    </div>
  );
}
